// Modèle d'humeur des Kazis (couche life-sim). Chaque Kazi a 5 jauges 0-100 qui décroissent
// avec le temps (sauf Notes). Décroissance PARESSEUSE : calculée à la lecture depuis le dernier
// accès (pas de timer). Restaurées plus tard par des interactions (cantine, CDI, etc.).
//
// Mapping Tomodachi -> LaCaze (cf. docs/.../game-design.md) :
//   Faim (hunger) · Énergie (energy) · Social · Fun · Notes (perf scolaire, ne décroît pas seule).

export interface Moods {
    faim: number
    energie: number
    social: number
    fun: number
    notes: number
}
export type MoodKey = keyof Moods

// Id d'humeur du JOUEUR (avatar). Les props interactifs modifient CES jauges (cf. HudScene).
export const PLAYER_ID = 'eleve-joueur'

export const MOOD_META: Array<{ key: MoodKey; label: string; color: number }> = [
    { key: 'faim', label: 'Faim', color: 0xf6ad55 },
    { key: 'energie', label: 'Énergie', color: 0x68d391 },
    { key: 'social', label: 'Social', color: 0x63b3ed },
    { key: 'fun', label: 'Fun', color: 0xf687b3 },
    { key: 'notes', label: 'Notes', color: 0xf6e05e },
]

// Temps (ms) pour qu'une jauge passe de 100 à 0. Rythme lent : les jauges ne se vident pas pendant
// le jeu normal mais comptent sur une session (~20-30 min). (À ajuster ici si besoin.)
const FULL_DECAY_MS: Moods = {
    faim: 20 * 60 * 1000,
    energie: 45 * 60 * 1000, // matches server: stamina drains slower than hunger (class/meals replenish it)
    social: 25 * 60 * 1000,
    fun: 25 * 60 * 1000,
    notes: 0, // ne décroît pas passivement
}

interface Stored { moods: Moods; t: number }
const store = new Map<string, Stored>()

const clamp = (n: number) => Math.max(0, Math.min(100, n))
const hash = (s: string) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h }

// Valeurs initiales déterministes par id (60-90 ; Notes 50-90) pour de la variété sans aléatoire.
function init(id: string): Stored {
    const h = hash(id)
    const v = (shift: number, base: number, span: number) => base + (((h >> shift) & 0xff) / 255) * span
    return {
        moods: {
            faim: v(0, 60, 30), energie: v(8, 60, 30), social: v(16, 55, 35),
            fun: v(24, 55, 35), notes: v(4, 50, 40),
        },
        t: Date.now(),
    }
}

function decayed(s: Stored): Moods {
    const dt = Date.now() - s.t
    const m = s.moods
    const step = (val: number, fullMs: number) => fullMs > 0 ? clamp(val - (100 / fullMs) * dt) : clamp(val)
    return {
        faim: step(m.faim, FULL_DECAY_MS.faim),
        energie: step(m.energie, FULL_DECAY_MS.energie),
        social: step(m.social, FULL_DECAY_MS.social),
        fun: step(m.fun, FULL_DECAY_MS.fun),
        notes: clamp(m.notes),
    }
}

/** Humeurs actuelles d'un Kazi (décroissance appliquée + persistée). */
export function getMoods(id: string): Moods {
    let s = store.get(id)
    if (!s) { s = init(id); store.set(id, s) }
    const cur = decayed(s)
    store.set(id, { moods: cur, t: Date.now() }) // persiste la valeur décrue, repart de maintenant
    return cur
}

/** Modifie une jauge (ex: nourrir au self -> +faim). delta peut être négatif. */
export function adjustMood(id: string, key: MoodKey, delta: number): void {
    const cur = getMoods(id)
    cur[key] = clamp(cur[key] + delta)
    store.set(id, { moods: cur, t: Date.now() })
}
